import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { meService, type MeMenuItem, type MeResponse } from '../api/services/me.service'
import { useAuth } from '../contexts/AuthContext'
import { withMockMenuItems } from './mockMenuItems'

interface PermissionContextType {
  isLoading: boolean
  permissions: Set<string>
  menu: MeMenuItem[]
  me: MeResponse | null
  hasPermission: (code: string) => boolean
  hasAnyPermission: (codes: string[]) => boolean
  refresh: () => Promise<void>
}

const PermissionContext = createContext<PermissionContextType | null>(null)

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [permissions, setPermissions] = useState<Set<string>>(new Set())
  const [menu, setMenu] = useState<MeMenuItem[]>([])
  const [me, setMe] = useState<MeResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions(new Set())
      setMenu([])
      setMe(null)
      return
    }
    setIsLoading(true)
    try {
      const [meData, permsData, menuData] = await Promise.all([
        meService.get(),
        meService.getPermissions(),
        meService.getMenu(),
      ])
      setMe(meData)
      setPermissions(new Set(permsData.permissions ?? []))
      setMenu(withMockMenuItems(menuData ?? []))
    } catch {
      setPermissions(new Set())
      setMenu([])
      setMe(null)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    refresh()
  }, [refresh])

  const hasPermission = useCallback((code: string) => permissions.has(code), [permissions])
  const hasAnyPermission = useCallback(
    (codes: string[]) => codes.some(c => permissions.has(c)),
    [permissions],
  )

  const value = useMemo<PermissionContextType>(
    () => ({ isLoading, permissions, menu, me, hasPermission, hasAnyPermission, refresh }),
    [isLoading, permissions, menu, me, hasPermission, hasAnyPermission, refresh],
  )

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePermissions() {
  const ctx = useContext(PermissionContext)
  if (!ctx) throw new Error('usePermissions must be used within PermissionProvider')
  return ctx
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHasPermission(code: string) {
  return usePermissions().hasPermission(code)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHasAnyPermission(codes: string[]) {
  return usePermissions().hasAnyPermission(codes)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMenu() {
  return usePermissions().menu
}
